import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, X, UserPlus, ArrowUp, MagnifyingGlass, Warning, Certificate, Trophy, PencilLine, Archive, ArrowCounterClockwise } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

const emptyClass = { name: "", course_id: "", teacher_id: "", schedule: "", room: "", max_capacity: 15 };

function ClassForm({ initial, courses, teachers, onSave, onClose }) {
  const { t } = useTranslation();
  const isEdit = !!initial?.id;
  const [f, setF] = useState(() => (initial ? { ...emptyClass, ...initial } : emptyClass));
  const [saving, setSaving] = useState(false);
  const setV = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    if (!f.name?.trim()) return toast.error(t("classes.form.nameRequired"));
    if (!f.course_id) return toast.error(t("classes.form.courseRequired"));
    if (!f.teacher_id) return toast.error(t("classes.form.teacherRequired"));
    setSaving(true);
    try {
      const payload = {
        ...f,
        name: f.name.trim(),
        max_capacity: Number(f.max_capacity) || 15,
      };
      const { data } = isEdit
        ? await api.put(`/classes/${initial.id}`, payload)
        : await api.post("/classes", payload);
      toast.success(isEdit ? t("classes.toasts.updated") : t("classes.toasts.created"));
      onSave(data);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("classes.form.name")}</Label>
        <Input value={f.name || ""} onChange={setV("name")} className="mt-1.5 h-10"
          placeholder={t("classes.form.namePh")} data-testid="class-field-name" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("classes.form.course")}</Label>
        <Select value={f.course_id || ""} onValueChange={(v) => setF({ ...f, course_id: v })}>
          <SelectTrigger className="mt-1.5 h-10" data-testid="class-field-course"><SelectValue placeholder={t("classes.form.selectCourse")} /></SelectTrigger>
          <SelectContent>
            {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.language} · {c.level})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("classes.form.teacher")}</Label>
        <Select value={f.teacher_id || ""} onValueChange={(v) => setF({ ...f, teacher_id: v })}>
          <SelectTrigger className="mt-1.5 h-10" data-testid="class-field-teacher"><SelectValue placeholder={t("classes.form.selectTeacher")} /></SelectTrigger>
          <SelectContent>
            {teachers.map((tc) => <SelectItem key={tc.id} value={tc.id}>{tc.full_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("classes.form.schedule")}</Label>
        <Input value={f.schedule || ""} onChange={setV("schedule")} className="mt-1.5 h-10"
          placeholder={t("classes.form.schedulePh")} data-testid="class-field-schedule" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("classes.form.room")}</Label>
        <Input value={f.room || ""} onChange={setV("room")} className="mt-1.5 h-10"
          placeholder={t("classes.form.roomPh")} data-testid="class-field-room" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("classes.form.maxCapacity")}</Label>
        <Input type="number" value={f.max_capacity ?? ""} onChange={setV("max_capacity")}
          className="mt-1.5 h-10" data-testid="class-field-capacity" />
      </div>
      <DialogFooter className="md:col-span-2">
        <Button variant="outline" onClick={onClose} data-testid="class-form-cancel">{t("common.cancel")}</Button>
        <Button onClick={submit} disabled={saving} data-testid="class-form-save">
          {saving ? t("common.saving") : (isEdit ? t("common.saveChanges") : t("classes.createBtn"))}
        </Button>
      </DialogFooter>
    </div>
  );
}

function BatchIssueDialog({ cls, students, onClose, onIssued }) {
  const { t } = useTranslation();
  const enrolled = cls.student_ids.map((id) => students.find((s) => s.id === id)).filter(Boolean);
  const [scores, setScores] = useState({});
  const [examName, setExamName] = useState(`${cls.name} Final Exam`);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [maxScore, setMaxScore] = useState(100);
  const [threshold, setThreshold] = useState(70);
  const [saving, setSaving] = useState(false);

  const setScore = (sid, v) => setScores((s) => ({ ...s, [sid]: v }));
  const setAll = (v) => {
    const next = {}; enrolled.forEach((s) => { next[s.id] = v; }); setScores(next);
  };

  const passing = enrolled.filter((s) => Number(scores[s.id] || 0) >= (maxScore * threshold / 100)).length;

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/classes/${cls.id}/batch-issue-certificates`, {
        exam_name: examName, date, max_score: maxScore, pass_threshold: threshold, scores,
      });
      toast.success(t("classes.toasts.issuedCount", { issued: data.issued, total: data.total }));
      onIssued(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 mt-2">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider font-semibold">{t("classes.batch.examName")}</Label>
          <Input value={examName} onChange={(e) => setExamName(e.target.value)} className="mt-1.5 h-10" data-testid="batch-exam-name" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold">{t("classes.batch.date")}</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 h-10" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs uppercase tracking-wider font-semibold">{t("classes.batch.max")}</Label>
            <Input type="number" value={maxScore} onChange={(e) => setMaxScore(Number(e.target.value))} className="mt-1.5 h-10" /></div>
          <div><Label className="text-xs uppercase tracking-wider font-semibold">{t("classes.batch.passPct")}</Label>
            <Input type="number" value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} className="mt-1.5 h-10" /></div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center text-xs">
        <span className="text-muted-foreground">{t("classes.batch.quickFill")}</span>
        {[100, 85, 70, 50, 0].map((v) => (
          <Button key={v} size="sm" variant="outline" onClick={() => setAll(v)}
            data-testid={`fill-${v}`}>{v}</Button>
        ))}
      </div>

      <div className="rounded-md border border-border/60 max-h-72 overflow-y-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 border-b border-border sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">{t("classes.batch.student")}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">{t("classes.batch.score")}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">{t("classes.batch.status")}</th>
            </tr>
          </thead>
          <tbody>
            {enrolled.map((s) => {
              const v = Number(scores[s.id] || 0);
              const passes = v >= (maxScore * threshold / 100);
              return (
                <tr key={s.id} className="border-b border-border last:border-0" data-testid={`batch-row-${s.id}`}>
                  <td className="px-3 py-2 font-semibold">{s.full_name}</td>
                  <td className="px-3 py-2 text-right">
                    <Input type="number" value={scores[s.id] ?? ""} onChange={(e) => setScore(s.id, e.target.value)}
                      className="h-8 w-20 ml-auto text-right" placeholder="—" />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {scores[s.id] !== undefined && scores[s.id] !== "" && (
                      <Badge className={passes ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground"}>
                        {passes ? t("classes.batch.pass") : t("classes.batch.fail")}
                      </Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between p-3 rounded-md bg-primary/5">
        <div className="text-sm">
          <span className="text-muted-foreground">{t("classes.batch.willIssue")} </span>
          <span className="font-mono font-bold text-primary">{passing}</span>
          <span className="text-muted-foreground"> {t("classes.batch.ofCertificates", { count: enrolled.length })}</span>
        </div>
        <Button onClick={submit} disabled={saving} data-testid="batch-issue-save" className="gap-2">
          <Certificate size={14} weight="duotone" /> {saving ? t("classes.batch.issuing") : t("classes.batch.issueCount", { count: passing })}
        </Button>
      </div>
    </div>
  );
}


function EnrollDialog({ cls, students, onSaved }) {
  const { t } = useTranslation();
  const [sid, setSid] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!sid) return toast.error(t("classes.enroll.selectStudent"));
    setSaving(true);
    try {
      const { data } = await api.post(`/classes/${cls.id}/enroll`, { student_id: sid });
      toast.success(data.status === "waitlisted" ? t("classes.toasts.waitlisted") : t("classes.toasts.enrolled"));
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const available = students.filter((s) => !cls.student_ids.includes(s.id) && !(cls.waiting_list || []).includes(s.id));
  return (
    <div className="space-y-3 mt-2">
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("classes.enroll.student")}</Label>
        <Select value={sid} onValueChange={setSid}>
          <SelectTrigger data-testid="enroll-student-select" className="mt-1.5 h-10"><SelectValue placeholder={t("classes.enroll.selectPh")} /></SelectTrigger>
          <SelectContent>{available.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} ({s.student_number})</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="text-xs text-muted-foreground">
        {t("classes.enroll.capacityInfo", { filled: cls.student_ids.length, cap: cls.max_capacity || 15 })}
        {cls.student_ids.length >= (cls.max_capacity || 15) && (
          <span className="ml-2 text-destructive font-semibold">{t("classes.enroll.classFullNote")}</span>
        )}
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="enroll-save">
          {saving ? t("classes.enroll.enrolling") : t("classes.enroll.enroll")}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function Classes() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const canManage = ["super_admin", "director", "branch_manager"].includes(user?.role);

  const load = async () => {
    const [c, s, cr, tc] = await Promise.all([
      api.get("/classes", { params: { archived: showArchived } }), api.get("/students"),
      api.get("/courses"), api.get("/teachers"),
    ]);
    setRows(c.data); setStudents(s.data); setCourses(cr.data); setTeachers(tc.data);
  };
  useEffect(() => { load(); }, [showArchived]);
  useEffect(() => {
    if (selected) {
      const fresh = rows.find((r) => r.id === selected.id);
      if (fresh) setSelected(fresh);
    }
    // eslint-disable-next-line
  }, [rows]);

  const archive = async (id, e) => {
    e.stopPropagation();
    try { await api.post(`/classes/${id}/archive`); toast.success(t("classes.toasts.archived")); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const restore = async (id, e) => {
    e.stopPropagation();
    try { await api.post(`/classes/${id}/restore`); toast.success(t("classes.toasts.restored")); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const studentName = (id) => students.find((s) => s.id === id)?.full_name || id.slice(0, 8);

  const unenroll = async (cid, sid) => {
    try {
      await api.post(`/classes/${cid}/unenroll`, { student_id: sid });
      toast.success(t("classes.toasts.unenrolled"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t("nav.classes")}</div>
          <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>{t("classes.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("classes.subtitle")}</p>
        </div>
        {canManage && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-class-btn" className="rounded-full gap-2">
                <Plus size={16} weight="bold" /> {t("classes.createBtn")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle style={{ fontFamily: "Manrope" }}>{t("classes.newClass")}</DialogTitle></DialogHeader>
              <ClassForm courses={courses} teachers={teachers}
                onSave={load} onClose={() => setCreateOpen(false)} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {canManage && (
        <div className="flex justify-end">
          <button
            data-testid="show-archived-classes"
            onClick={() => setShowArchived((v) => !v)}
            className={`h-9 px-3 rounded-full text-xs font-semibold border transition-colors flex items-center gap-2 ${showArchived ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
          >
            <Archive size={12} weight={showArchived ? "fill" : "regular"} />
            {showArchived ? t("common.hideArchived") : t("common.showArchived")}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((c) => {
          const cap = c.max_capacity || 15;
          const filled = c.student_ids.length;
          const wl = c.waiting_list?.length || 0;
          const pct = Math.min(100, (filled / cap) * 100);
          const full = filled >= cap;
          return (
            <Card key={c.id} className="border-border/60 card-hover cursor-pointer"
              onClick={() => setSelected(c)} data-testid={`class-card-${c.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold" style={{ fontFamily: "Manrope" }}>{c.name}</h3>
                    <div className="text-xs text-muted-foreground mt-1">{c.schedule}</div>
                    <div className="text-xs text-muted-foreground">{c.room}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {full && <Badge variant="destructive" className="gap-1"><Warning size={10} /> {t("classes.card.full")}</Badge>}
                    {c.archived && <Badge variant="secondary" className="text-[10px]">{t("common.archived")}</Badge>}
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-muted-foreground">{t("classes.card.enrolled")}</span>
                    <span className="font-mono font-bold">{filled}/{cap}</span>
                  </div>
                  <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div className={cn("h-full rounded-full transition-all duration-300",
                      full ? "bg-destructive" : "bg-primary")}
                      style={{ width: `${pct}%` }} />
                  </div>
                  {wl > 0 && (
                    <div className="mt-2 text-[11px] text-amber-600 dark:text-amber-400 font-semibold">
                      {t("classes.card.onWaitingList", { count: wl })}
                    </div>
                  )}
                </div>
                {canManage && (
                  <div className="mt-3 pt-3 border-t border-border flex justify-end">
                    {c.archived ? (
                      <Button size="sm" variant="outline" onClick={(e) => restore(c.id, e)}
                        data-testid={`restore-class-${c.id}`} className="gap-1.5 h-8 text-xs">
                        <ArrowCounterClockwise size={12} weight="bold" /> {t("common.restore")}
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={(e) => archive(c.id, e)}
                        data-testid={`archive-class-${c.id}`} className="gap-1.5 h-8 text-xs text-muted-foreground hover:text-destructive">
                        <Archive size={12} weight="bold" /> {t("common.archive")}
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {selected && (
        <Dialog open onOpenChange={() => setSelected(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Manrope" }}>{selected.name}</DialogTitle>
              <div className="text-xs text-muted-foreground">{selected.schedule} · {selected.room}</div>
            </DialogHeader>

            {canManage && (
              <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader><DialogTitle style={{ fontFamily: "Manrope" }}>{t("classes.editClass", { defaultValue: "Edit Class" })}</DialogTitle></DialogHeader>
                  <ClassForm initial={selected} courses={courses} teachers={teachers}
                    onSave={() => load()} onClose={() => setEditOpen(false)} />
                </DialogContent>
              </Dialog>
            )}

            <div className="flex items-center justify-between mt-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("classes.detail.capacity")}</div>
                <div className="text-2xl font-bold font-mono">{selected.student_ids.length}/{selected.max_capacity || 15}</div>
              </div>
            <div className="flex items-center gap-2">
              {canManage && (
                <Button data-testid="edit-class-btn" variant="outline" className="rounded-full gap-2"
                  onClick={() => setEditOpen(true)}>
                  <PencilLine size={14} weight="bold" /> {t("common.edit", { defaultValue: "Edit" })}
                </Button>
              )}
              <Dialog open={dialog} onOpenChange={setDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-enrollment-btn" className="rounded-full gap-2"><UserPlus size={14} weight="bold" /> {t("classes.detail.enrollStudent")}</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t("classes.detail.enrollInto", { name: selected.name })}</DialogTitle></DialogHeader>
                  <EnrollDialog cls={selected} students={students} onSaved={() => { setDialog(false); load(); }} />
                </DialogContent>
              </Dialog>
              <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="batch-issue-btn" variant="outline" className="rounded-full gap-2">
                    <Trophy size={14} weight="duotone" /> {t("classes.detail.batchIssue")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: "Manrope" }}>{t("classes.detail.batchIssueTitle", { name: selected.name })}</DialogTitle>
                    <div className="text-xs text-muted-foreground">{t("classes.detail.batchIssueDesc")}</div>
                  </DialogHeader>
                  <BatchIssueDialog cls={selected} students={students} onClose={() => setBatchOpen(false)} onIssued={load} />
                </DialogContent>
              </Dialog>
            </div>
            </div>

            <div className="mt-6">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">{t("classes.detail.enrolledStudents")}</h3>
              <ul className="divide-y divide-border border border-border/60 rounded-md">
                {selected.student_ids.map((sid) => (
                  <li key={sid} data-testid={`enrolled-${sid}`} className="flex items-center justify-between px-3 py-2">
                    <div className="text-sm font-semibold">{studentName(sid)}</div>
                    <Button size="sm" variant="ghost" onClick={() => unenroll(selected.id, sid)}
                      data-testid={`unenroll-${sid}`} className="text-destructive">
                      <X size={14} />
                    </Button>
                  </li>
                ))}
                {selected.student_ids.length === 0 && <li className="p-4 text-sm text-muted-foreground text-center">{t("classes.detail.noStudents")}</li>}
              </ul>
            </div>

            {selected.waiting_list?.length > 0 && (
              <div className="mt-6">
                <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-2">
                  {t("classes.detail.waitingList")} <Badge variant="secondary">{selected.waiting_list.length}</Badge>
                </h3>
                <ul className="divide-y divide-border border border-amber-500/40 bg-amber-500/5 rounded-md">
                  {selected.waiting_list.map((sid, idx) => (
                    <li key={sid} data-testid={`waitlist-${sid}`} className="flex items-center justify-between px-3 py-2">
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold">{idx + 1}</div>
                        <div className="text-sm font-semibold">{studentName(sid)}</div>
                      </div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-widest">
                        <ArrowUp size={10} weight="bold" className="inline mr-1" />
                        {t("classes.detail.nextInLine")}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
